import { Link } from "react-router-dom";
import { Trash2, Flag } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api";
import StatsGrid from "./StatsGrid";
import DeviceStatsStrip from "./DeviceStatsStrip";
import Avatar from "./Avatar";
import VerifiedBadge from "./VerifiedBadge";
import CommentsSection from "./CommentsSection";
import PostMedia from "./PostMedia";
import PostActions from "./PostActions";
import { ReportDialog } from "./ReportDialog";
import { useAuth } from "../context/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

export default function PostCard({ post, onDelete }) {
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [commentsCount, setCommentsCount] = useState(post.comments_count);
  const [reportOpen, setReportOpen] = useState(false);

  const doDelete = async () => {
    if (!confirm("Delete this post?")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      onDelete?.(post.id);
    } catch (err) {
      console.error("Failed to delete post:", err);
    }
  };

  const author = post.author || {};
  const isOwner = user && user.id === post.user_id;

  return (
    <article
      data-testid={`post-card-${post.id}`}
      className="fade-up bg-[#121212] border border-[#262626] rounded-sm relative"
    >
      <header className="flex items-center gap-3 p-4">
        <Link to={`/profile/${author.id}`} data-testid={`post-author-link-${post.id}`}>
          <Avatar url={author.avatar_url} name={author.name} size={44} />
        </Link>
        <div className="flex-1 min-w-0">
          <Link
            to={`/profile/${author.id}`}
            className="text-white font-semibold text-sm hover:underline inline-flex items-center gap-1.5"
          >
            {author.name}
            <VerifiedBadge role={author.role} verified={author.verified} />
          </Link>
          <div className="text-xs text-[#a3a3a3]">
            {author.position ? `${author.position}` : ""}
            {author.team ? (
              <>
                {author.position ? " · " : ""}
                <Link
                  to={`/team/${encodeURIComponent(author.team)}`}
                  className="hover:text-white hover:underline"
                >
                  {author.team}
                </Link>
              </>
            ) : null}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-0.5">
            {timeAgo(post.created_at)}
            {post.season ? (
              <span data-testid={`post-season-${post.id}`}> · {post.season}</span>
            ) : null}
          </div>
        </div>
        {isOwner && (
          <button
            data-testid={`post-delete-${post.id}`}
            onClick={doDelete}
            className="p-2 text-[#737373] hover:text-red-500 hover:bg-[#1a1a1a] rounded-sm transition-colors"
            aria-label="Delete post"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        {!isOwner && user && (
          <button
            data-testid={`post-report-${post.id}`}
            onClick={() => setReportOpen(true)}
            className="p-2 text-[#737373] hover:text-white hover:bg-[#1a1a1a] rounded-sm transition-colors"
            aria-label="Report post"
            title="Report this post"
          >
            <Flag className="w-4 h-4" />
          </button>
        )}
      </header>

      {reportOpen && (
        <ReportDialog
          targetType="post"
          targetId={post.id}
          targetLabel={`Post by ${author.name}`}
          onClose={() => setReportOpen(false)}
        />
      )}

      {post.caption && (
        <p className="px-4 pb-3 text-[15px] text-white/90 whitespace-pre-wrap font-body leading-relaxed">
          {post.caption}
        </p>
      )}

      <PostMedia post={post} />

      <div className="px-4 pb-4">
        <StatsGrid
          goals={post.goals}
          assists={post.assists}
          minutes={post.minutes}
          opponent={post.opponent}
          result={post.result}
        />
      </div>

      <DeviceStatsStrip stats={post.device_stats} postId={post.id} />

      <PostActions
        post={post}
        commentsCount={commentsCount}
        onToggleComments={() => setShowComments((s) => !s)}
      />

      {showComments && (
        <CommentsSection
          postId={post.id}
          onCountChange={setCommentsCount}
        />
      )}
    </article>
  );
}
